<template>
  <div>
    <V2StakeModal
      :dialog="dialogs.stake"
      :model="modalSelectedModel"
      :button="false"
      :full-width="true"
      :models="formattedModels"
      :on-close="() => onModalClose('stake')"
    />
    <DiagnosticsModal
      :dialog="dialogs.diagnostics"
      :diagnostics-id="pickleDiagnosticsId"
      :model="modalSelectedModel"
      :button="false"
      :models="formattedModels"
      :on-close="() => onModalClose('diagnostics')"
    />
    <ComputeModal
      :dialog="dialogs.compute"
      :model="modalSelectedModel"
      :button="false"
      :on-close="() => onModalClose('compute')"
      :on-set-webhook="fetchAccountInfo"
    />
    <AddModelModal
      :dialog="dialogs.new"
      :button="false"
      :on-close="() => onModalClose('new')"
    />
    <RenameModelModal
      :dialog="dialogs.rename"
      :model="modalSelectedModel"
      :button="false"
      :on-close="() => onModalClose('rename')"
    />
    <ModelUploadModal
      :dialog="dialogs.pickleSetup"
      :pickle="selectedModelPickle"
      :pickles="pickles"
      :model="modalSelectedModel"
      :docker-images="dockerImages"
      :data-versions="pickleDataVersions"
      :button="false"
      :on-close="() => onModalClose('pickleSetup')"
      :on-update-success="onModelUpdateSuccess"
      :on-disable="onPickleDisable"
      :is-validating="
        modalSelectedModel && triggerIsLoading(modalSelectedModel)
      "
    />
    <PickleUploadLogModal
      :dialog="dialogs.triggerLogs"
      :on-close="() => onModalClose('triggerLogs')"
      :pickle="selectedModelPickle"
    />
    <DiagnosticsLogsModal
      :dialog="dialogs.diagnosticsLogs"
      :pickle-id="diagnosticsLogsPickleId"
      :error-description="diagnosticsLogsErrorDescription"
      :on-close="() => onModalClose('diagnosticsLogs')"
    />
    <div v-if="isLoadingAccountInfo" class="pa-10">
      <NumeraiLoader />
    </div>
    <div v-else>
      <div class="flexrow space_between pl-6 pt-5 pr-3 mb-10">
        <div>
          <h1 class="numerai-section-heading models-heading">
            Models ({{ accountInfo.models.length }})
          </h1>
        </div>
        <div class="flexrow align_center">
          <div
            v-if="
              view === 'submissions' &&
                currentRoundInfo &&
                currentRoundInfo.number
            "
          >
            <p class="mb-0 mr-9">
              Round {{ currentRoundInfo.number }} models submitted:
              {{ numberOnTimeSubmissions }} /
              {{ accountInfo.models.length }}
            </p>
          </div>
          <div class="search mr-2">
            <v-icon small class="search-icon">mdi-magnify</v-icon>
            <input v-model="search" class="search-input" placeholder="Search" />
          </div>
          <button
            v-if="view === 'overview'"
            class="numerai-button button-subtle button-control mr-2"
            @click="() => openModal('new')"
          >
            <v-icon small>mdi-plus</v-icon>&nbsp;New Model
          </button>
        </div>
      </div>
      <div v-if="accountInfo.models.length > 0" class="pl-1 pr-1 mb-2">
        <!-- Models table -->
        <v-data-table
          class="models-table"
          :class="{ dark: isDarkMode, 'theme--dark': isDarkMode }"
          :headers="modelsTableHeaders"
          :header-props="{
            'sort-icon': 'mdi-menu-down',
          }"
          :custom-sort="sortModels"
          :items="formattedModels"
          :items-per-page="itemsPerPage"
          :dark="isDarkMode"
          :search="search"
          @update:items-per-page="updateItemsPerPage"
        >
          <template v-slot:item.name="{ item }">
            <div class="model-name-mobile">
              <router-link :to="'/' + item.name" class="text_decoration_none">
                <div class="model-data numerai-link no-arrow">
                  {{ item.name && item.name.toUpperCase() }}
                </div>
              </router-link>
            </div>
            <div class="model-info pl-2">
              <router-link :to="'/' + item.name" class="avatar" tag="a">
                <ProfileImage :src="item.profileUrl" />
              </router-link>
              <div class="ml-2">
                <router-link :to="'/' + item.name" class="text_decoration_none">
                  <div class="modelname">{{ item.name }}</div>
                </router-link>
              </div>
            </div>
          </template>
          <template v-slot:item.stakeValue="{ item }">
            <div v-if="item.stakeValue">
              <NumeraiCurrency
                :sign="false"
                :value="item.stakeValue"
                :tooltip-usd="false"
                class="stake model-data font-number"
              />
              <br />
            </div>
            <div v-else class="null-number"></div>
            <div v-if="item.changeRequestDrain" class="drain model-data">
              <NumeraiTooltip :text="drainTooltip(item)">
                Drain
              </NumeraiTooltip>
            </div>
            <div v-else-if="item.changeRequestAmount" class="model-data">
              <NumeraiCurrency
                v-if="item.changeRequestType == 'increase'"
                :sign="true"
                :value="item.changeRequestAmount"
                :tooltip-usd="false"
                class="model-data"
              />
              <NumeraiCurrency
                v-else
                :sign="true"
                :value="-item.changeRequestAmount"
                :tooltip-usd="false"
                :custom-tooltip="stakeChangeTooltip(item)"
                class="model-data leftStakeSpacer"
              />
            </div>
          </template>
          <template v-slot:item.multipliers="{ item }">
            <div v-if="item.stakeValue > 0">
              <div
                v-if="formatStakeType(item, 'corrMultiplier', 'CORR')"
                class="model-data"
              >
                {{ formatStakeType(item, 'corrMultiplier', 'CORR') }}
              </div>
              <div
                v-if="formatStakeType(item, 'tcMultiplier', 'TC')"
                class="model-data"
              >
                <span class="color-text-tc">
                  {{ formatStakeType(item, 'tcMultiplier', 'TC') }}
                </span>
              </div>
              <div
                v-if="formatStakeType(item, 'mmcMultiplier', 'MMC')"
                class="model-data"
              >
                <span class="color-text-mmc">
                  {{ formatStakeType(item, 'mmcMultiplier', 'MMC') }}
                </span>
              </div>
            </div>
            <div v-else class="null-number"></div>
          </template>
          <template v-slot:item.payout="{ item }">
            <div v-if="item.stakeValue > 0" class="model-data status">
              <div>{{ payoutMode(item) }}</div>
            </div>
            <div v-else class="null-number"></div>
          </template>
          <template v-slot:item.return1d="{ item }">
            <div
              class="model-data"
              :class="{
                delta: item.return1d,
                'null-number': !item.return1d,
                'color-positive': parseFloat(item.return1d) > 0,
                'color-negative': parseFloat(item.return1d) < 0,
              }"
            >
              {{ removeMinusSign(formatPercent(item.return1d)) }}
            </div>
          </template>
          <template v-slot:item.return3m="{ item }">
            <div
              class="model-data"
              :class="{
                delta: item.return3m,
                'null-number': !item.return3m,
                'color-positive': parseFloat(item.return3m) > 0,
                'color-negative': parseFloat(item.return3m) < 0,
              }"
            >
              {{ removeMinusSign(formatPercent(item.return3m)) }}
            </div>
          </template>
          <template v-slot:item.return1y="{ item }">
            <div
              class="model-data"
              :class="{
                delta: item.return1y,
                'null-number': !item.return1y,
                'color-positive': parseFloat(item.return1y) > 0,
                'color-negative': parseFloat(item.return1y) < 0,
              }"
            >
              {{ removeMinusSign(formatPercent(item.return1y)) }}
            </div>
          </template>
          <template v-slot:item.returnAll="{ item }">
            <div
              class="model-data"
              :class="{
                delta: item.returnAll,
                'null-number': !item.returnAll,
                'color-positive': parseFloat(item.returnAll) > 0,
                'color-negative': parseFloat(item.returnAll) < 0,
              }"
            >
              {{ removeMinusSign(formatPercent(item.returnAll)) }}
            </div>
          </template>
          <!-- eslint-disable -->
          <!-- https://stackoverflow.com/questions/62511252/vuetify-data-table-combine-dynamic-slots-with-row-item-slots -->
          <!-- eslint-enable -->
          <template
            v-for="h in modelsTableHeaders"
            v-slot:[`header.${h.value}`]="{ header }"
          >
            <v-tooltip v-if="h.tooltip" :key="h.text" bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ h.text }}</span>
              </template>
              <span>{{ h.tooltip }}</span>
            </v-tooltip>
            <span v-else :key="h.text + h.tooltip">{{ h.text }}</span>
          </template>
          <template
            v-for="(obj, scoreKey, idx) in tournamentScoresConfig"
            v-slot:[`item.${scoreKey}`]="{ item }"
          >
            <div :key="item.id + scoreKey">
              <div
                v-if="item[scoreKey]"
                class="model-data"
                :class="{
                  tc: scoreKey === 'tc',
                  leftSpacer: leftSpacer(formatScore(item[scoreKey])),
                  negative: formatScore(item[scoreKey]) < 0,
                }"
              >
                {{ formatScore(item[scoreKey]) }}
              </div>
              <div
                v-else
                :key="item.id + scoreKey + idx"
                class="null-number"
              ></div>
            </div>
          </template>
          <template v-slot:item.status="{ item }">
            <RecentRoundsStatuses
              :latest-submissions="item.latestSubmissions"
            />
          </template>
          <template v-slot:item.submissions="{ item }">
            <RecentRoundsStatuses
              :latest-submissions="item.latestSubmissions"
              :expanded="true"
            />
          </template>
          <template v-slot:item.uploadStatus="{ item }">
            <div
              v-if="isFetchingPickles"
              class="d-flex align_center justify_center pickle-status-loader"
            >
              <NumeraiLoader class="text-left" />
            </div>
            <div
              v-else-if="
                item.computePickleUpload && item.computePickleUpload.id
              "
            >
              <StatusChip
                :loading="triggerIsLoading(item)"
                :text="triggerStatusText(item)"
                :status="triggerStatusClass(item)"
                :fixed-width="true"
              />
              <div class="d-flex justify-center">
                <div class="flex_col align_center">
                  <div class="mt-2">
                    <a @click="openModal('triggerLogs', item)">View logs</a>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="null-number"></div>
          </template>
          <template v-slot:item.diagnostics="{ item }">
            <div
              v-if="isFetchingPickles"
              class="d-flex align_center justify_center pickle-status-loader"
            >
              <NumeraiLoader class="text-left" />
            </div>
            <div v-else-if="getPickleFromModel(item)" class="d-flex">
              <div class="flex_col align_center">
                <StatusChip
                  :text="diagnosticsStatusText(item)"
                  :loading="pickleDiagnosticsLoading(item)"
                  :status="diagnosticsStatus(item)"
                  :fixed-width="true"
                />
                <div class="mt-2">
                  <a
                    v-if="pickleDiagnosticsLogsAvailable(item)"
                    @click="showDiagnosticsLogs(item)"
                    >View logs
                  </a>
                  <a
                    v-if="pickleDiagnosticsAvailable(item)"
                    @click="showPickleDiagnostics(item)"
                  >
                    View diagnostics</a
                  >
                </div>
              </div>
            </div>
            <div v-else class="null-number"></div>
          </template>
          <template v-slot:item.pickleInfo="{ item }">
            <div v-if="getPickleFromModel(item)">
              <div>
                Filename:
                {{ trimFilenameHash(getPickleFromModel(item).filename) }}
              </div>
              <div>
                Created:
                {{ moment(getPickleFromModel(item).insertedAt).format('l LT') }}
              </div>
            </div>
            <div v-else class="null-number"></div>
          </template>
          <template v-slot:item.filename="{ item }">
            <div v-if="filename(item)" class="model-data filename">
              <NumeraiTooltip :text="formatFilename(filename(item), true)">
                {{ formatFilename(filename(item)) }}
              </NumeraiTooltip>
            </div>
            <div v-else class="null-number"></div>
          </template>
          <template v-slot:item.uploaded="{ item }">
            <div v-if="uploadedTime(item)" class="model-data">
              {{ uploadedTime(item) }}
            </div>
            <div v-else class="null-number"></div>
          </template>
          <template v-slot:item.liveTickers="{ item }">
            <div v-if="tickerCount(item)" class="model-data">
              {{
                `${tickerCount(item)}${
                  currentRoundInfo.numTickers
                    ? ` / ${currentRoundInfo.numTickers}`
                    : ''
                }`
              }}
            </div>
            <div v-else class="null-number"></div>
          </template>
          <template v-slot:item.invalidTickers="{ item }">
            <div
              class="model-data"
              :class="{ 'null-number': !invalidTickerCount(item) }"
            >
              {{ invalidTickerCount(item) }}
            </div>
          </template>
          <template v-slot:item.manage="{ item }">
            <div class="d-flex flex-row more-buttons">
              <v-btn
                v-if="showMenuItem('rename')"
                text
                :dark="isDarkMode"
                class="more-button"
                @click="openModal('rename', item)"
              >
                <NumeraiTooltip text="Rename Model">
                  <v-icon>mdi-pencil</v-icon>
                </NumeraiTooltip>
              </v-btn>
              <v-btn
                v-if="showMenuItem('stake')"
                text
                :dark="isDarkMode"
                class="more-button "
                @click="openModal('stake', item)"
              >
                <NumeraiTooltip text="Manage Stake">
                  <v-icon>mdi-tune</v-icon>
                </NumeraiTooltip>
              </v-btn>
              <v-btn
                v-if="showMenuItem('diagnostics')"
                text
                :dark="isDarkMode"
                class="more-button "
                @click="openModal('diagnostics', item)"
              >
                <NumeraiTooltip text="Run Diagnostics">
                  <v-icon>mdi-flask-outline</v-icon>
                </NumeraiTooltip>
              </v-btn>
              <v-btn
                v-if="showMenuItem('uploadModel') && isAppClassic()"
                text
                :dark="isDarkMode"
                class="more-button"
                :disabled="isFetchingPickles"
                @click="
                  openModal('pickleSetup', item)
                  track('clicked', 'upload model')
                "
              >
                <NumeraiTooltip text="Upload Model">
                  <v-icon>mdi-cloud-upload-outline</v-icon>
                </NumeraiTooltip>
              </v-btn>
              <v-btn
                v-if="showMenuItem('compute')"
                text
                :dark="isDarkMode"
                class="more-button"
                :disabled="pickleExists(item.id) || isFetchingPickles"
                @click="openModal('compute', item)"
              >
                <NumeraiTooltip text="Manage Compute">
                  <v-icon>mdi-memory</v-icon>
                </NumeraiTooltip>
              </v-btn>
              <NumeraiUploadFile
                v-if="showMenuItem('uploadPredictions')"
                :dark="isDarkMode"
                :auth-endpoint="getSubmissionUploadAuth"
                :create-endpoint="createSubmission"
                :model-id="item.id"
                :full-width="true"
                :disabled="pickleExists(item.id, true) || isFetchingPickles"
                success-text="Submission accepted"
                @click="track('clicked', 'upload prediction')"
              >
                <v-btn
                  text
                  :dark="isDarkMode"
                  class="more-button"
                  :disabled="pickleExists(item.id, true) || isFetchingPickles"
                >
                  <NumeraiTooltip text="Upload Submission">
                    <v-icon>mdi-upload</v-icon>
                  </NumeraiTooltip>
                </v-btn>
              </NumeraiUploadFile>
              <v-btn
                v-if="showMenuItem('copyModelId')"
                v-clipboard:copy="item.id"
                v-clipboard:success="onCopySuccess"
                v-clipboard:error="onCopyError"
                text
                :dark="isDarkMode"
                class="more-button "
                @click="track('clicked', 'copy model id')"
              >
                <NumeraiTooltip text="Copy Model ID">
                  <v-icon>mdi-content-copy</v-icon>
                </NumeraiTooltip>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import V2StakeModal from '@/components/shared/V2StakeModal'
import AddModelModal from '@/components/account/AddModelModal'
import {
  isAppSignals,
  isAppClassic,
  getTournamentNumber,
  getTournamentScoresConfig,
  removeMinusSign,
  formatPercent,
  formatScore,
  trimFilenameHash,
} from '@/lib/utils'
import Analytics from '@/lib/analytics'
import _ from 'lodash'
import ls from 'local-storage'
import moment from 'moment'

export default {
  components: {
    NumeraiLoader: () => import('@/components/shared/NumeraiLoader'),
    NumeraiUploadFile: () => import('@/components/shared/NumeraiUploadFile'),
    DiagnosticsModal: () => import('@/components/shared/DiagnosticsModal'),
    DiagnosticsLogsModal: () =>
      import('@/components/shared/DiagnosticsLogsModal'),
    ComputeModal: () => import('@/components/shared/ComputeModal'),
    RenameModelModal: () => import('@/components/shared/RenameModelModal'),
    ModelUploadModal: () => import('@/components/shared/ModelUploadModal'),
    ProfileImage: () => import('@/components/shared/ProfileImage'),
    NumeraiCurrency: () => import('@/components/shared/NumeraiCurrency'),
    StatusChip: () => import('@/components/shared/StatusChip'),
    RecentRoundsStatuses: () =>
      import('@/components/shared/RecentRoundsStatuses'),
    NumeraiTooltip: () => import('@/components/shared/NumeraiTooltip'),
    PickleUploadLogModal: () =>
      import('@/components/shared/PickleUploadLogModal'),
    AddModelModal,
    V2StakeModal,
  },
  props: {
    view: { type: String, default: 'overview' },
    localCacheKey: { type: String, default: 'models_table_items_per_page' },
  },
  data() {
    return {
      selectedModelId: null,
      modalSelectedModel: null,
      itemsPerPage: 10,
      search: '',
      isUploadingModel: [],
      dockerImages: [],
      pickleDataVersions: [],
      pickleDiagnosticsId: null,
      diagnosticsLogsPickleId: null,
      diagnosticsLogsErrorDescription: null,
      diagnosticsPollKey: {},
      dialogs: {
        new: false,
        rename: false,
        stake: false,
        diagnostics: false,
        diagnosticsLogs: false,
        compute: false,
        pickleSetup: false,
        triggerLogs: false,
      },
      defaultModelColumns: {
        name: {
          text: 'NAME',
          align: 'start',
          sortable: true,
          value: 'name',
          class: 'pl-6',
        },
        stake: {
          text: 'STAKE',
          align: 'start',
          sortable: true,
          value: 'stakeValue',
        },
        manage: {
          align: 'right',
          sortable: false,
          value: 'manage',
          class: 'pr-6',
          width: '70px',
        },
      },
    }
  },
  computed: {
    ...mapState([
      'accountInfo',
      'currentRoundInfo',
      'isFetchingPickles',
      'pickles',
      'pickleDiagnostics',
    ]),
    ...mapGetters(['isDarkMode']),
    isLoadingAccountInfo() {
      return !this.accountInfo.username
    },
    tournamentScoresConfig() {
      return getTournamentScoresConfig(getTournamentNumber(), 'showModelsTable')
    },
    tableCacheKey() {
      return `${this.localCacheKey}${getTournamentNumber()}`
    },
    modelsTableHeaders() {
      let score_headers = Object.entries(this.tournamentScoresConfig).map(
        ([scoreKey, scoreObj]) => {
          return {
            text: scoreObj.chartLabel.toUpperCase(),
            tooltip: scoreObj.scoreTooltip,
            align: 'start',
            sortable: true,
            value: scoreKey,
            payout: scoreObj.payout,
          }
        },
      )
      switch (this.view) {
        case 'staking':
          return [
            this.defaultModelColumns.name,
            this.defaultModelColumns.stake,
            ...score_headers.filter(score => score.payout),
            {
              text: 'MULTIPLIERS\n',
              align: 'start',
              sortable: false,
              value: 'multipliers',
            },
            {
              text: 'PAYOUT\n',
              align: 'start',
              sortable: false,
              value: 'payout',
            },
            {
              text: '1D',
              align: 'start',
              sortable: true,
              value: 'return1d',
            },
            {
              text: '3M',
              align: 'start',
              sortable: true,
              value: 'return3m',
            },
            {
              text: '1Y',
              align: 'start',
              sortable: true,
              value: 'return1y',
            },
            {
              text: 'ALL',
              align: 'start',
              sortable: true,
              value: 'returnAll',
            },
            this.defaultModelColumns.manage,
          ]
        case 'submissions':
          return [
            this.defaultModelColumns.name,
            {
              text: 'SUBMISSIONS',
              align: 'start',
              sortable: false,
              value: 'submissions',
            },
            ...(this.isAppClassic()
              ? [
                  {
                    text: 'LATEST SUBMISSION',
                    align: 'start',
                    sortable: false,
                    value: 'uploadStatus',
                  },
                ]
              : []),
            ...(this.isAppClassic()
              ? [
                  {
                    text: 'DIAGNOSTICS',
                    align: 'start',
                    sortable: false,
                    value: 'diagnostics',
                  },
                ]
              : []),
            ...(this.isAppClassic()
              ? [
                  {
                    text: 'AUTOMATION INFO',
                    align: 'start',
                    sortable: false,
                    value: 'pickleInfo',
                  },
                ]
              : []),
            this.defaultModelColumns.manage,
          ]
        case 'scores':
          return [
            this.defaultModelColumns.name,
            ...score_headers,
            this.defaultModelColumns.stake,
            this.defaultModelColumns.manage,
          ]
        default:
          // Overview
          return [
            this.defaultModelColumns.name,
            ...score_headers,
            this.defaultModelColumns.stake,
            {
              text: '1D',
              align: 'start',
              sortable: true,
              value: 'return1d',
            },
            {
              text: 'STATUS',
              align: 'start',
              sortable: false,
              value: 'status',
            },
            ...(this.isAppClassic()
              ? [
                  {
                    text: 'LATEST SUBMISSION',
                    align: 'start',
                    sortable: false,
                    value: 'uploadStatus',
                  },
                ]
              : []),
            this.defaultModelColumns.manage,
          ]
      }
    },
    numberOnTimeSubmissions() {
      if (
        !this.accountInfo ||
        !this.accountInfo.models ||
        !this.currentRoundInfo.number
      ) {
        return null
      }

      let counter = 0

      for (const model of this.accountInfo.models) {
        const submittedInCurrentRound = model.latestSubmissions.filter(
          submission => {
            return (
              submission.roundNumber === this.currentRoundInfo.number &&
              submission.status !== 'none'
            )
          },
        ).length
        if (submittedInCurrentRound) {
          counter++
        }
      }

      return counter
    },
    selectedModelPickle() {
      const model = this.modalSelectedModel

      if (!model) {
        return
      }

      return this.getPickleFromModel(model)
    },
    formattedModels() {
      let m = this.accountInfo.models

      if (!m) {
        return []
      }

      m = m.map(model => ({
        id: model.id,
        name: model.name,
        profileUrl: model.profileUrl,
        latestValue: _.get(model, 'v2Stake.latestValue'),
        stakeValue: _.get(model, 'v2Stake.stakeValue'),
        currentPayoutSelection: _.get(model, 'currentPayoutSelection'),
        ...Object.fromEntries(
          Object.keys(this.tournamentScoresConfig).map(scoreKey => {
            let latestUserScores = _.get(model, 'latestUserScores')
            if (!latestUserScores) {
              return [scoreKey, null]
            }
            let matchKey = scoreKey
            if (this.tournamentScoresConfig[scoreKey].backendName) {
              matchKey = this.tournamentScoresConfig[scoreKey].backendName
            }
            let score = model.latestUserScores.find(
              score => score.displayName === matchKey,
            )
            return [scoreKey, _.get(score, 'reputation', [])]
          }),
        ),
        return1d: _.get(model, 'returns.oneDay'),
        return3m: _.get(model, 'returns.threeMonths'),
        return1y: _.get(model, 'returns.oneYear'),
        returnAll: _.get(model, 'returns.allTime'),
        changeRequestType: _.get(
          model,
          'v2Stake.pendingV2ChangeStakeRequest.type',
        ),
        changeRequestAmount: _.get(
          model,
          'v2Stake.pendingV2ChangeStakeRequest.requestedAmount',
        ),
        changeRequestDrain: _.get(
          model,
          'v2Stake.pendingV2ChangeStakeRequest.drain',
        ),
        latestSubmission: model.latestSubmission,
        ...model,
      }))

      return m
    },
    modelUploadAuthEndpoint() {
      return this.$client.getModelUploadAuth
    },
    modelUploadCreateEndpoint() {
      return this.$client.createModelUpload
    },
    errorTriggerStatus() {
      return [null, 'model_failed', 'submission_failed', 'submission_error']
    },
  },
  async created() {
    const cachedItemsPerPage = ls.get(this.tableCacheKey)
    if (cachedItemsPerPage) {
      this.itemsPerPage = cachedItemsPerPage
    }
    if (!this.accountInfo.username) {
      await this.fetchAccountInfo()
    }
    if (this.view === 'overview' || this.view === 'submissions') {
      this.fetchComputePickleDockerImages()
      // call fetchAllModelUploads every 30 seconds
      this.fetchAllModelUploadsInterval = setInterval(
        this.fetchAllModelUploads,
        30000,
      )
    }
    const searchParams = new URLSearchParams(location.search)
    const modelId = searchParams.get('modelId')
    const diagnosticsId = searchParams.get('diagnosticsId')
    if (modelId && diagnosticsId) {
      this.selectedModelId = modelId
      this.modalSelectedModel = this.accountInfo.models.filter(
        model => model.id === modelId,
      )[0]
      this.pickleDiagnosticsId = diagnosticsId
      this.openModal('diagnostics')
    }
  },
  beforeDestroy() {
    clearInterval(this.fetchAllModelUploadsInterval)
  },
  methods: {
    ...mapActions([
      'fetchAccountInfo',
      'fetchAllModelUploads',
      'showSnackBar',
      'closeSnackBar',
    ]),
    removeMinusSign,
    formatPercent,
    formatScore,
    isAppSignals,
    isAppClassic,
    moment,
    trimFilenameHash,
    test(test) {
      return test
    },
    getPickle(id) {
      return this.pickles.find(pickle => pickle.id === id)
    },
    getSubmissionUploadAuth({ filename, modelId }) {
      return this.$client.getSubmissionAuth({
        filename,
        modelId,
        tournament: getTournamentNumber(),
      })
    },
    createSubmission({ filename, modelId }) {
      return this.$client.createSubmission({
        filename,
        modelId,
        tournament: getTournamentNumber(),
      })
    },
    getPickleFromModel(model) {
      if (!model) {
        return null
      }

      const pickleId = model.computePickleUpload && model.computePickleUpload.id

      if (!pickleId) {
        return null
      }

      let pickle = this.pickles.find(pickle => pickle.id === pickleId)
      return pickle
    },
    pickleExists(modelId, includeInvalid) {
      const pickle = this.pickles.find(pickle => pickle.modelId === modelId)

      if (!includeInvalid || !pickle) {
        return !!pickle
      }

      if (
        (includeInvalid && pickle.validationStatus === 'invalid') ||
        pickle.validationStatus === 'failed'
      ) {
        return false
      }

      return !!pickle
    },
    showPickleDiagnostics(model) {
      this.pickleDiagnosticsId = model.diagnosticsId
      this.openModal('diagnostics', model)
    },
    async fetchComputePickleDockerImages() {
      this.dockerImages = await this.$client.getComputePickleDockerImages()
      this.pickleDataVersions = await this.$client.getComputePickleDataVersions()
    },
    onModelUploadStart(modelId) {
      this.isUploadingModel.push(modelId)
    },
    onModelUploadEnd(modelId) {
      this.isUploadingModel = this.isUploadingModel.filter(
        item => item !== modelId,
      )
    },
    isPickleUploading(modelId) {
      return this.isUploadingModel[modelId]
    },
    async onModelUpdateSuccess(model) {
      const pickleId = model.computePickleUpload
        ? model.computePickleUpload.id
        : null

      // Clear the previous diagnostics poll if it exists
      if (pickleId) {
        clearTimeout(this.diagnosticsPollKey[pickleId])
      }
      this.fetchAllModelUploads()
    },
    onPickleDisable() {
      window.location.reload()
    },
    onModalClose(modalName) {
      this.dialogs[modalName] = false
      this.selectedModelId = null
      this.pickleDiagnosticsId = null
      this.modalSelectedModel = null
      if (modalName !== 'diagnostics') {
        this.fetchAccountInfo()
      }
    },
    openModal(modalName, model) {
      if (model && model.id) {
        this.selectedModelId = model.id
        this.modalSelectedModel = model
      }
      this.dialogs[modalName] = true
    },
    updateItemsPerPage(number) {
      this.itemsPerPage = number
      ls.set(this.tableCacheKey, number)
    },
    sortModels(items, sortBy, sortAsc) {
      const sortedItems = [...items]

      let scoreKey = this.getScoreKey(sortBy[0])
      if (
        [
          'return1d',
          'return3m',
          'return1y',
          'returnAll',
          'stakeValue',
        ].includes(sortBy[0])
      ) {
        scoreKey = sortBy[0]
      }
      const convertStringToFloat = sortBy[0] === 'stakeValue'

      if (scoreKey) {
        sortedItems.sort((a, b) => {
          const aVal =
            convertStringToFloat && a[scoreKey]
              ? parseFloat(a[scoreKey])
              : a[scoreKey]
          const bVal =
            convertStringToFloat && b[scoreKey]
              ? parseFloat(b[scoreKey])
              : b[scoreKey]
          if (!sortAsc[0]) {
            if (aVal == null) {
              return 1
            } else if (bVal == null) {
              return -1
            } else {
              return bVal < aVal ? -1 : 1
            }
          } else {
            if (aVal == null) {
              return 1
            } else if (bVal == null) {
              return -1
            } else {
              return aVal < bVal ? -1 : 1
            }
          }
        })
      }

      if (sortBy[0] === 'name') {
        sortedItems.sort((a, b) => {
          if (!sortAsc[0]) {
            return b.name < a.name ? -1 : 1
          } else {
            return a.name < b.name ? -1 : 1
          }
        })
      }

      return sortedItems
    },
    getScoreKey(displayName) {
      const scoresConfig = this.tournamentScoresConfig
      const scoreKey = Object.keys(scoresConfig).filter(key => {
        return scoresConfig[key].displayName === displayName
      })
      return scoreKey[0]
    },
    automationMode(model) {
      if (!model.computeEnabled) {
        return false
      } else if (model.computeEnabled) {
        return 'Compute'
      } else if (model.computeLiteEnabled) {
        return 'Compute Lite'
      }
    },
    formatTooltipDate(model) {
      const releaseDate = _.get(
        model,
        'v2Stake.pendingV2ChangeStakeRequest.dueDate',
      )
      return moment(new Date(releaseDate))
        .utc()
        .format('YYYY-MM-DD')
    },
    formatStakeType(model, key, multiplier) {
      const selection = _.get(model, 'currentPayoutSelection')
      let stake = _.get(selection, key)

      // change the display of the multipliers for clarity
      if (stake) {
        if (multiplier === 'CORR' && isAppSignals()) {
          multiplier = 'FNCv4'
        } else if (multiplier === 'CORR' && isAppClassic()) {
          multiplier = 'CORR20'
        }

        return stake + 'X' + multiplier
      }
    },
    stakeChangeTooltip(model) {
      return `Pending release on ${this.formatTooltipDate(model)}`
    },
    drainTooltip(model) {
      return `Pending drain on ${this.formatTooltipDate(model)}`
    },
    payoutMode(model) {
      return _.get(model, 'currentPayoutSelection.takeProfit', false)
        ? 'Take Profit'
        : 'Compound'
    },
    leftSpacer(score) {
      return score < 0
    },
    formatFilename(name, showFullName) {
      const formattedName = trimFilenameHash(name)

      if (formattedName.length <= 31 || showFullName) {
        return formattedName
      } else {
        return [
          ...formattedName.slice(0, 14),
          '...',
          ...formattedName.slice(
            formattedName.length - 14,
            formattedName.length,
          ),
        ].join('')
      }
    },
    onSubmissionUploaded() {
      this.fetchAccountInfo()
    },
    onCopySuccess() {
      this.showSnackBar({ msg: 'Model ID copied to clipboard' })
    },
    onCopyError() {
      this.showSnackBar({ msg: 'Failed to copy: ' + this.modelId, time: 0 })
    },
    triggerIsLoading(model) {
      let pickle = this.getPickleFromModel(model)
      if (!pickle) {
        return false
      }
      let status = pickle.triggerStatus
      return (
        !this.errorTriggerStatus.includes(status) &&
        status !== 'submission_succeeded'
      )
    },
    latestTrigger(model) {
      // sort triggers by insertedAt and get latest one
      let pickle = this.pickles.find(
        pickle => pickle.id === model.computePickleUpload.id,
      )
      return pickle.triggers.sort(
        (a, b) => new Date(b.insertedAt) - new Date(a.insertedAt),
      )[0]
    },
    triggerIsValidating(model) {
      let pickle = this.getPickleFromModel(model)
      if (!pickle) {
        return null
      }
      return ['pending', 'validating'].includes(pickle.triggerStatus)
    },
    triggerStatusClass(model) {
      let pickle = this.getPickleFromModel(model)
      if (!pickle) {
        return null
      }
      let status = pickle.triggerStatus
      if (this.errorTriggerStatus.includes(status)) {
        return 'error'
      } else if (status === 'submission_succeeded') {
        return 'success'
      } else {
        return 'active'
      }
    },
    triggerStatusText(model) {
      let pickle = this.getPickleFromModel(model)
      if (!pickle) {
        return null
      }
      switch (pickle.triggerStatus) {
        case null:
          return 'Error'
        case 'queued':
          return 'Queued'
        case 'running':
          return 'Running'
        case 'model_succeeded':
          return 'Validating'
        case 'model_failed':
          return 'Failed'
        case 'submission_succeeded':
          return 'Success'
        case 'submission_failed':
          return 'Invalid'
        case 'submission_error':
          return 'Error'
      }
    },
    diagnosticsStatusText(model) {
      let pickle = this.getPickleFromModel(model)
      if (!pickle) {
        return null
      }
      switch (pickle.diagnosticsStatus) {
        case 'running':
          return 'Running'
        case 'pending':
          return 'Pending'
        case 'failed':
          return 'Failed'
        case 'succeeded':
          return 'Success'
        default:
          return ''
      }
    },
    diagnosticsStatus(model) {
      let pickle = this.getPickleFromModel(model)
      if (!pickle) {
        return null
      }
      switch (pickle.diagnosticsStatus) {
        case 'running':
          return 'active'
        case 'pending':
          return 'active'
        case 'failed':
          return 'error'
        case 'succeeded':
          return 'success'
        default:
          return ''
      }
    },
    pickleDiagnosticsLoading(model) {
      let status = this.getPickleFromModel(model).diagnosticsStatus
      return status === 'running' || status === 'pending'
    },
    pickleDiagnosticsAvailable(model) {
      return this.getPickleFromModel(model).diagnosticsStatus === 'succeeded'
    },
    pickleDiagnosticsLogsAvailable(model) {
      return this.getPickleFromModel(model).diagnosticsStatus === 'failed'
    },
    showDiagnosticsLogs(model) {
      const pickle = this.getPickleFromModel(model)
      this.diagnosticsLogsPickleId = pickle.id
      this.diagnosticsLogsErrorDescription = pickle.diagnosticsStatusDescription
      this.openModal('diagnosticsLogs')
    },
    showMenuItem(menuItem) {
      if (this.view === 'overview') {
        return ['rename', 'uploadModel', 'copyModelId'].includes(menuItem)
      }

      if (this.view === 'staking') {
        return ['stake'].includes(menuItem)
      }

      if (this.view === 'submissions') {
        return [
          'uploadModel',
          'uploadPredictions',
          'compute',
          'copyModelId',
        ].includes(menuItem)
      }

      if (this.view === 'scores' && getTournamentNumber() !== 12) {
        return ['diagnostics'].includes(menuItem)
      }
    },
    track(action, description) {
      Analytics.track('model onboarding', {
        tournament: getTournamentNumber(),
        action,
        description,
      })
    },
  },
}
</script>

<style scoped>
.models-heading {
  display: inline-block;
}

.search {
  display: flex;
  align-items: center;
  border: 1px solid var(--color-button-control-background-border);
  border-radius: 4px;
  padding: 10px 5px;
  max-height: 40px;
}

.search-icon {
  color: var(--color-secondary);
}

.search-icon.theme--light.v-icon {
  color: var(--color-primary) !important;
}

.search-input {
  color: var(--color-secondary);
  margin-left: 10px;
  font-size: 16px;
}

.search-input:focus {
  outline: none;
}

.button-control {
  background-color: var(--color-button-control-background);
  border-color: var(--color-button-control-background-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.models-table.dark {
  background-color: var(--paper-background-color);
}

.model-data {
  font-family: var(--number-font);
  white-space: nowrap;
  font-size: 14px;
  color: var(--color-secondary);
}

.model-data.negative {
  margin-left: -8px;
}

.pickle-status-loader {
  width: 145px;
}

.stake {
  color: var(--color-text-nmr);
}

.model-info {
  display: none;
  min-width: 190px;
  height: 64px;
  align-items: center;
}

.avatar {
  padding-right: 8px;
}

td.modelname {
  text-align: left;
}

td {
  padding: 0 10px;
}

td.data {
  font-family: var(--number-font);
  padding-top: 8px;
  padding-bottom: 8px;
}

td.status {
  vertical-align: center;
  font-family: var(--ui-font);
  font-weight: 500;
}

.modelname {
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  text-transform: uppercase;
  cursor: pointer;
  overflow-wrap: never;
  font-family: var(--brand-font);
  color: var(--color-secondary);
  vertical-align: center;
  display: flex;
  align-items: center;
  margin-bottom: -3px;
}

.tc {
  color: var(--color-true-contribution);
}

.leftStakeSpacer {
  margin-left: -10px;
}

.filename {
  white-space: nowrap;
  text-align: left;
}

.more-button {
  display: flex;
  padding: 0 8px !important;
  min-width: 30px !important;
}

.more-button .v-icon {
  color: var(--color-secondary);
  font-size: 20px;
}

/* Overrides the default v-data-table line colors */
.theme--light.v-data-table >>> th,
.theme--light.v-data-table >>> td:not(.v-data-table__mobile-row) {
  border-bottom: thin solid rgba(0, 0, 0, 0.09) !important;
}

.theme--light.v-data-table >>> .v-data-footer {
  border-top: none;
}

@media screen and (min-width: 600px) {
  .search-input {
    width: 250px;
  }

  .model-info {
    display: flex;
  }

  .model-name-mobile {
    display: none;
  }

  .more-button {
    display: block;
  }
}

.loading-spinner {
  animation: loader 1s infinite linear;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
